import React, { Component, ReactNode, ErrorInfo } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from './button'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onReset?: () => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Error Boundary component for catching and handling React errors
 * Wraps components to provide graceful error handling
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({
      error,
      errorInfo,
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
    this.props.onReset?.()
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            textAlign: 'center',
            gap: '1rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#DC2626',
            }}
          >
            <AlertCircle style={{ width: '1.5rem', height: '1.5rem' }} />
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
              Something went wrong
            </h2>
          </div>
          <p style={{ color: '#6B7280', margin: 0 }}>
            We encountered an error while loading this component.
          </p>
          {this.state.error && (
            <details style={{ textAlign: 'left', maxWidth: '100%' }}>
              <summary style={{ cursor: 'pointer', color: '#6B7280', marginBottom: '0.5rem' }}>
                Error details
              </summary>
              <pre
                style={{
                  padding: '1rem',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '0.5rem',
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxWidth: '100%',
                }}
              >
                {this.state.error.toString()}
                {this.state.errorInfo && `\n\n${this.state.errorInfo.componentStack}`}
              </pre>
            </details>
          )}
          <Button onClick={this.handleReset} style={{ display: 'flex', gap: '0.5rem' }}>
            <RefreshCw style={{ width: '1rem', height: '1rem' }} />
            Try Again
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}
